import { OrderDirection } from '@granlupo/api-client/generated/content/zeus'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/dist/client/router'
import NextImage from 'next/legacy/image'
import { useCallback, useEffect, useState, type ChangeEventHandler } from 'react'
import { useLoadMore } from '../../hooks/useLoadMore'
import { RespondWithNotFound, serverPropsHandler } from '../../libs/next/api/handlers'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import { websiteContent } from '../../libs/next/loaders/graphql/src/contentClient'
import { CheckboxGroupComponent } from '../../src/components/form/CheckboxGroupComponent'
import { AdContent } from '../../src/components/shared/AdContent'
import { Button } from '../../src/components/shared/Button'
import { Container } from '../../src/components/shared/Container'
import { Hidden } from '../../src/components/shared/Hidden'
import { Intro } from '../../src/components/shared/Intro'
import { Layout } from '../../src/components/shared/Layout'
import { ListingProducts } from '../../src/components/shared/ListingProducts'
import { LoginModal } from '../../src/components/shared/LoginModal'
import { ProductEmptyState } from '../../src/components/shared/ProductEmptyState'
import { ProductLineCardGroup } from '../../src/components/shared/ProductLineCardGroup'
import { SuggestionModal } from '../../src/components/shared/SuggestionModal'
import { TitleEnumeration } from '../../src/components/shared/TitleEnumeration'
import { EndOfListBanner } from '../../src/components/static/EndOfListBanner'
import { toArray } from '../../src/const/ArrayHelper'
import { FilterQuery } from '../../src/const/FilterQuery'
import { InternalLink } from '../../src/const/Links'
import { END_LIST_CARD_VISIBLE_AMOUNT } from '../../src/const/digitContants'
import { setSeoBrand } from '../../src/const/setSeo'
import { Translation, useTranslate } from '../../src/contexts/TranslationContext'
import { AdFragment } from '../../src/data/AdFragment'
import { BrandDetailFragment, BrandVisualFragment } from '../../src/data/BrandFragment'
import { DidYouKnowFragment } from '../../src/data/DidYouKnowFragment'
import { ItemSubCategoryFragment, ItemSubCategoryPreviewFragment } from '../../src/data/ItemSubCategoryFragment'
import { LayoutFragment } from '../../src/data/LayoutFragment'
import { loadBrandProducts } from '../../src/loaders/loadBrandProducts'
import style from '../../src/styles/BrandPageDetail.module.sass'
import { roundTheCurrentTimeToMinutes } from '../../src/utils/roundTheCurrentTimeToMinutes'
import { useDidYouKnowCheck } from '../../src/utils/useDidYouKnowCheck'
import type { API } from '../api/loaders/[handler]'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>

const api = createFetcher<API>('/api/loaders/[handler]')

export default function GenericPage(props: GenericPageProps) {
	const [search, setSearch] = useState({ flavor: [] as string[] })

	const brand = props.data.getBrand

	const [searchValue, setSearchValue] = useState('')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)

	const router = useRouter()
	const translate = useTranslate()

	const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		setSearchValue(event.currentTarget.value)
	}

	const handleClose = useCallback(() => {
		setSearchValue('')
	}, [setSearchValue])

	useEffect(() => {
		setSearch({
			flavor: toArray(router.query.flavor),
		})
	}, [router])

	const query = useLoadMore(
		['products', props.brandId, JSON.stringify(search), searchValue],
		props.listingProducts,
		async (page) => api('brandProducts', { _req: { query: { page, id: props.brandId } }, search, searchValue }),
	)

	const allProducts = query.data?.pages.flatMap((page) => page.items)

	const notNullProductLines = brand?.productLines.filter((line) => line.paginateProducts.pageInfo.totalCount > 0)
	const { preview } = router.query

	return (
		<Layout
			hasBreadcrumbs
			didYouKnow={useDidYouKnowCheck(props.data.listDidYouKnow)}
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={setSeoBrand(brand)}
			url={`${InternalLink.Brand}${props.data.getBrand?.slug}`}
		>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />
			<Container size="wide">
				<div className={style.content}>
					<div className={style.container}>
						{brand?.logo && (
							<Container size="narrow">
								<div className={style.logoWrapper}>
									<NextImage
										src={brand.logo.url}
										alt={translate('alternativeText.brand')}
										title={brand.name}
										layout="fill"
										objectFit="contain"
									/>
								</div>
							</Container>
						)}
						<Hidden breakpoint="mobileDown">
							<AdContent ads={props.data.listAd ?? []} withContentAbove={Boolean(brand?.logo)} isAlignedRight />
						</Hidden>
					</div>
					<Container size="narrow" disableGutters>
						{brand && (
							<>
								<Intro
									title={
										<>
											<Translation tr="brandPage.title" />
											<br />
											<span className={style.greenColor}> {brand.name}</span>
										</>
									}
									subtitle={brand.lead}
									lead={preview ? (brand?.draft?.blocks ?? []) : (brand?.content?.blocks ?? [])}
									leftAligned
								/>
							</>
						)}
						{notNullProductLines && notNullProductLines.length > 0 && (
							<ProductLineCardGroup
								productLines={notNullProductLines}
								title={translate('brandPage.productLines.title')}
							/>
						)}

						{brand?.products && brand.products.length > 0 ? (
							<>
								{props.listFlavors && props.listFlavors.length > 0 && (
									<div className={style.filterWrapper}>
										<CheckboxGroupComponent
											title={translate('brands.section.checkboxGroup.title')}
											items={props.listFlavors}
											isFilter
											sum={query.data?.pages[0].totalCount ?? 0}
											initialQuery={props.initialQuery}
											label="flavor"
										/>
									</div>
								)}
								<TitleEnumeration
									title={translate('brands.section.titleEnumeration.title')}
									sum={query.data?.pages[0].totalCount ?? 0}
									buttonLabel={translate('productLine.section.titleEnumeration.button')}
									onChange={handleChange}
									searchedText={searchValue}
									handleClose={handleClose}
								/>
								{allProducts && allProducts.length > 0 ? (
									<>
										<ListingProducts
											products={allProducts}
											allAllergens={props.listAllergens}
											onNextPage={query.fetchNextPage}
											hasNextPage={Boolean(query.hasNextPage)}
											totalCount={query.data?.pages[0]?.totalCount ?? 0}
											openModal={() => setIsLoginModalOpen(true)}
										/>
										{Number(query.data?.pages[0]?.totalCount) - allProducts.length < END_LIST_CARD_VISIBLE_AMOUNT && (
											<EndOfListBanner />
										)}
									</>
								) : (
									<ProductEmptyState
										favouriteBrands={props.data.listBrand}
										title={translate('brands.section.emptyState.title')}
										subtitle={
											<span className={style.emptyStateButton}>
												<Translation tr="brandPage.emptyState.subtitle.first" />{' '}
												<Button
													type={{ variant: 'underlined' }}
													label={translate('brandPage.emptyState.button')}
													onClick={() => setIsModalOpen(true)}
												/>{' '}
												<Translation tr="emptyState.subtitle.second" />
											</span>
										}
									/>
								)}
							</>
						) : (
							<ProductEmptyState
								favouriteBrands={props.data.listBrand}
								title={translate('brands.section.emptyState.title')}
								subtitle={
									<span className={style.emptyStateButton}>
										<Translation tr="brandPage.emptyState.subtitle.first" />{' '}
										<Button
											type={{ variant: 'underlined' }}
											label={translate('brandPage.emptyState.button')}
											onClick={() => setIsModalOpen(true)}
										/>{' '}
										<Translation tr="emptyState.subtitle.second" />
									</span>
								}
							/>
						)}
					</Container>
				</div>
			</Container>
			{isLoginModalOpen && <LoginModal closeModal={() => setIsLoginModalOpen(false)} isModalOpen={isLoginModalOpen} />}
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler(async (context: GetServerSidePropsContext) => {
	context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')

	const content = websiteContent

	const brandSlug = String(context.query.brand_slug)

	const data = await content('query')({
		...LayoutFragment(),
		listAd: [
			{
				filter: {
					isActive: { eq: true },
					activeFrom: { lte: roundTheCurrentTimeToMinutes() },
					activeUntil: { gte: roundTheCurrentTimeToMinutes() },
					or: [{ isBrandSectionVisible: { eq: true } }, { brands: { slug: { in: [brandSlug] } } }],
				},
			},
			AdFragment(),
		],
		getBrand: [
			{
				by: {
					slug: brandSlug,
				},
			},
			BrandDetailFragment(),
		],
		listBrand: [
			{
				limit: 7,
				orderBy: [{ promotedOnBrandsPagePlace: OrderDirection.asc }],
				filter: {
					not: {
						slug: { eq: brandSlug },
					},
					promotedOnBrandsPagePlace: { notEq: 0 },
				},
			},
			BrandVisualFragment(),
		],

		listDidYouKnow: [
			{
				limit: 40,
				orderBy: [
					{
						_random: true,
					},
				],
			},

			DidYouKnowFragment(),
		],
	})

	const getBrand = data.getBrand

	if (!getBrand) {
		throw new RespondWithNotFound('Značka nenalezena')
	}
	const subCategories = await content('query')({
		__alias: {
			allergens: {
				listItemSubCategory: [
					{
						orderBy: [{ remoteId: OrderDirection.asc }],
						filter: { isMostCommonAllergenTag: { eq: true } },
					},
					ItemSubCategoryFragment(),
				],
			},
			flavors: {
				listItemSubCategory: [
					{
						orderBy: [{ remoteId: OrderDirection.asc }],
						filter: {
							isFlavorCategory: { eq: true },
							products: {
								id: {
									or: getBrand.products.map((product) => ({ in: [product.id] })),
								},
							},
						},
					},
					ItemSubCategoryPreviewFragment(),
				],
			},
		},
	})

	return {
		props: {
			data,
			brandId: data?.getBrand?.id,
			initialQuery: FilterQuery.parse(context.query),
			listingProducts: await loadBrandProducts(data?.getBrand?.id, 1),
			listAllergens: subCategories.allergens.listItemSubCategory,
			listFlavors: subCategories.flavors.listItemSubCategory,
		},
	}
})
